import Dates from '../Dates/Dates';
import Select from '../Select/Select';
import Tools from '../Tools/Tools';

/**
 * Třída pro vytváření kalendáře
 * How to implement:
 * - Add class js-Calendar to the element containing the string of date (input or some text element)
 * - (optional) Add attribute data-calendar-allow-interval. It can have no value. This allows selecting date interval.
 * - (optional) Add attribute data-calendar-allow-past. It can have no value. This allows selecting past dates.
 * - (optional) Add attribute data-calendar-allow-future. It can have no value. This allows selecting future dates.
 * - (optional) Add attribute data-calendar-container="{String}" with selector string of the element to cantain Calendar component.
 *   First tries to find closest ancestor element. If omitted, parent element is selected.
 * - (optional) Add attribute data-calendar-period-option="{String}" with value from:
 *   today, yesterday, week, lastweek, last7, lastworkweek, month, lastmonth, last6months, quarter, last4quarters, last30, year, lastyear, whole
 * - (optional) It is possible to place child nodes to the .js-Calendar element:
 *   - .js-Calendar-start - Start date of inteval or single date
 *   - .js-Calendar-end - End date of interval
 */
class Calendar {
	/**
	 * @param {HTMLElement} dateElement HTML Element, který obsahuje datum a používá Calendar
	 */
	constructor(dateElement) {
		this.dateElement = dateElement;

		this.allowInterval = this.dateElement.dataset.calendarAllowInterval !== undefined;

		this.allowPast = this.dateElement.dataset.calendarAllowPast !== undefined;

		this.allowFuture = this.dateElement.dataset.calendarAllowFuture !== undefined;

		// find calendar container
		const containerSelector = this.dateElement.dataset.calendarContainer;

		if (containerSelector) {
			this.calendarContainer = this.dateElement.closest(containerSelector) || document.querySelector(containerSelector);
		} else {
			this.calendarContainer = this.dateElement.parentNode;
		}

		// find if start and end are separated elements
		this.startDateElement = this.dateElement.querySelector('.js-Calendar-start');

		this.endDateElement = this.dateElement.querySelector('.js-Calendar-end');
		this.separated = !!(this.startDateElement && this.endDateElement);

		// set listeners
		if (this.dateElement && this.calendarContainer) {
			this.visibleRange = {};

			this.listenOnButtonClick();
			this.listenOnOutsideClick();
		}

		// set today
		this.today = new Date();
		this.today.setHours(0, 0, 0, 0);
	}

	/**
	 * Najde elementy, které mají otevírat kalendář
	 * Statická Factory Method
	 */
	static init() {
		const dateElements = document.querySelectorAll('.js-Calendar');

		for (const element of dateElements) {
			// eslint-disable-next-line no-new
			new Calendar(element);
		}
	}

	/**
	 * Naslouchá na stisk tlačítka nebo pole kalendáře na stránce a
	 * otevře/zavře kalendář.
	 */
	listenOnButtonClick() {
		this.dateElement.addEventListener('click', this.toggleCalendar.bind(this));
	}

	/**
	 * Odstraní kalendář z DOMu.
	 * @param {Element} calendarWrap Element obsahující celý kalendář.
	 */
	removeCalendar(calendarWrap) {
		this.table1 = null;
		this.table2 = null;
		this.table3 = null;
		this.calendarContainer.removeChild(calendarWrap);
	}

	/**
	 * Pokud uživatel změnil časový úsek, tak komponenty graf/tabulka budou notifikovány.
	 */
	notifyChange() {
		if (this.dateFrom !== this.originalDateFrom || this.dateTo !== this.originalDateTo) {
			const eventDetail = {
				dateFrom: Dates.timestampToAPIFormat(this.dateFrom),
				dateTo: null,
				element: this.dateElement,
				interval: null,
			};

			if (this.allowInterval) {
				eventDetail.dateTo = Dates.timestampToAPIFormat(this.dateTo);
				eventDetail.interval = this.select.getSelectedOption();
			}

			const event = new CustomEvent('calendar-changed', {
				detail: eventDetail,
			});

			window.dispatchEvent(event);
		}
	}

	/**
	 * Vyrenderuje kalendář do stránky.
	 * Pokud je otevřen, tak ho odstraní.
	 */
	toggleCalendar(e) {
		if (e) {
			e.preventDefault();
		}

		if (!this.dateElement.classList.toggle('is-open')) {
			this.removeCalendar(this.calendarContainer.querySelector('.Calendar'));

			return;
		} else {
			const calendarWrap = Tools.createElement('div', { class: 'Calendar' });

			// Vybírám datum OD určitého termínu.
			this.selectFrom = true;

			// Sekce s 3 tabulkami.
			this.calendarMonthsWrap = Tools.createElement('div', {
				class: 'Calendar-months',
			});

			this.calendarMonthsWrap.addEventListener('click', this.daySelect.bind(this));

			// Sekce s tlačítky/inputy.
			const calendarFormWrap = Tools.createElement('div', {
				class: 'Calendar-inputs',
			});

			this.calendarContainer.appendChild(calendarWrap);

			// Kompletace.
			Tools.appendChilds(calendarWrap, this.calendarMonthsWrap, calendarFormWrap);

			this.getDateFromButton();
			this.generatePrevNextButtons();
			this.generateForm(calendarFormWrap);
			this.setVisibleRange();
			this.renderMonths();
			this.setInputs();
		}
	}

	/**
	 * Nastaví do inputů aktuálně zvolené datum DD.MM.YYYY
	 */
	setInputs() {
		this.inputFrom.value = Dates.timestampToString(this.dateFrom);

		if (this.allowInterval) {
			this.inputTo.value = Dates.timestampToString(this.dateTo);
		}
	}

	/**
	 * Viditelné měsíce se nastavují, tak že poslední aktivní den
	 * je v druhé tabulce (uprostřed).
	 * např. datum 2.2017 - 11.2017, tak listopad bude v druhé tabulce.
	 */
	setVisibleRange() {
		const lastDay = this.dateTo || this.dateFrom || this.today;

		this.visibleRange.second = {
			month: Dates.getMonthFromTimestamp(lastDay),
			year: Dates.getYearFromTimestamp(lastDay),
		};

		this.visibleRange.first = Dates.dateMinusMonth(this.visibleRange.second.month, this.visibleRange.second.year);

		this.visibleRange.third = Dates.datePlusMonth(this.visibleRange.second.month, this.visibleRange.second.year);
	}

	/**
	 * Posune kalendář (3 tabulky/měsíce) o měsíc vpřed.
	 */
	forwardVisibleRange() {
		this.visibleRange.third = Dates.datePlusMonth(this.visibleRange.third.month, this.visibleRange.third.year);

		this.visibleRange.second = Dates.datePlusMonth(this.visibleRange.second.month, this.visibleRange.second.year);

		this.visibleRange.first = Dates.datePlusMonth(this.visibleRange.first.month, this.visibleRange.first.year);
	}

	/**
	 * Posune kalendář (3 tabulky/měsíce) o měsíc zpět.
	 */
	backwardVisibleRange() {
		this.visibleRange.third = Dates.dateMinusMonth(this.visibleRange.third.month, this.visibleRange.third.year);

		this.visibleRange.second = Dates.dateMinusMonth(this.visibleRange.second.month, this.visibleRange.second.year);

		this.visibleRange.first = Dates.dateMinusMonth(this.visibleRange.first.month, this.visibleRange.first.year);
	}

	/**
	 * Získá datum od/do z tlačítka kalendáře.
	 */
	getDateFromButton() {
		let start = null;
		let end = null;

		if (this.separated) {
			start = this.getTextOrValue(this.dateElement.querySelector('.js-Calendar-start'));

			end = this.getTextOrValue(this.dateElement.querySelector('.js-Calendar-end'));
		} else {
			const entireDate = this.getTextOrValue(this.dateElement);
			const regex = /([\d\.\s*]+\d)\s*([-–—]\s*([\d\.\s*]+))?/g;
			const matches = regex.exec(entireDate);

			if (matches) {
				// eslint-disable-next-line valid-typeof
				start = typeof matches[1] !== undefined ? matches[1] : null;
				// eslint-disable-next-line valid-typeof
				end = typeof matches[3] !== undefined ? matches[3] : null;
			}
		}

		this.dateFrom = start ? Dates.stringToTimestamp(start) : null;
		this.dateTo = end ? Dates.stringToTimestamp(end) : null;
		// Uloží datum aby bylo možné zjistit zda se datum změnilo.
		this.originalDateFrom = this.dateFrom;
		this.originalDateTo = this.dateTo;
	}

	/**
	 * Přečte obsah elementu (value v případě input, innerHtml v případě jiných)
	 * @param {HTMLElement} element
	 * @returns {String} Obsah elementu
	 */
	getTextOrValue(element) {
		let content = null;

		switch (element.tagName.toLowerCase()) {
			case 'input':
				content = element.value;

				break;
			default:
				content = element.innerText;
		}

		return content;
	}

	/**
	 * Zapíše obsah do elementu (value v případě input, innerHtml v případě jiných)
	 * @param {HTMLElement} element Cílový element
	 * @param {String} content Text k zapsání do elementu
	 */
	setTextOrValue(element, content) {
		switch (element.tagName.toLowerCase()) {
			case 'input':
				element.value = content;

				break;
			default:
				element.innerText = content;
		}
	}

	/**
	 * Posune kalendář o měsíc dopředu.
	 */
	nextMonth() {
		this.forwardVisibleRange();
		this.renderMonths();
	}

	/**
	 * Posune kalendář o měsíc zpět.
	 */
	prevMonth() {
		this.backwardVisibleRange();
		this.renderMonths();
	}

	/**
	 * Pokud je input nevalidní, tak budou
	 * zrušeny vybrané dny.
	 */
	cancelActive() {
		Array.from(this.calendarMonthsWrap.querySelectorAll('.Calendar .Calendar-day.is-active')).forEach((day) => {
			day.classList.remove('is-active');
		});
	}

	/**
	 * Nestačí validovat pouze aktuálně upravovaný input.
	 * Může nastat situace, že upravovaný input není validní (23.3 - 12.3),
	 * ale úpravou druhého inputu se stane validním (11.3 - 12.3).
	 * @param {Element} input Input, který bude validován.
	 * @param {number} timestamp
	 */
	validateSecondInput(input, timestamp) {
		if (this.dateFrom <= this.dateTo && this._isYearValid(Dates.getYearFromTimestamp(timestamp))) {
			input.classList.remove('is-invalid');
			input.valid = true;
			this.renderMonths();
		} else {
			input.classList.add('is-invalid');
			input.valid = false;
		}
	}

	/**
	 * Reaguje na změnu data uživatelem pomocí inputu OD.
	 * Hlídá, jestli je string validní -> formát DD.MM.YYYY
	 * Hlídá, jestli se data nepřekrývají.
	 * Hlídá, jestli splňuje podmínky pro minulá a budoucí data (allowPast, allowFuture)
	 * @param {Event} e Event vyvolaný při změně inputu.
	 */
	changeDateInputFrom(e) {
		this.dateFrom = Dates.stringToTimestamp(e.target.value);

		if (this.allowInterval) {
			this.select.setSelectOption('other');
		}

		let isValid = true;

		if (!this.isStringFromInputValid(e.target.value)) {
			isValid = false;
		} else if (this.allowInterval && this.dateFrom > this.dateTo) {
			isValid = false;
		} else if (!this.allowPast && this.dateFrom < this.today) {
			isValid = false;
		} else if (!this.allowFuture && this.dateFrom > this.today) {
			isValid = false;
		}

		if (isValid) {
			this.inputFrom.classList.remove('is-invalid');
			this.inputFrom.valid = true;

			if (this.allowInterval) {
				this.validateSecondInput(this.inputTo, this.dateTo);
			} else {
				this.renderMonths();
			}
		} else {
			this.inputFrom.classList.add('is-invalid');
			this.inputFrom.valid = false;
			this.cancelActive();
		}
	}

	/**
	 * Reaguje na změnu data uživatelem pomocí inputu DO.
	 * Hlídá, jestli je string validní -> formát DD.MM.YYYY
	 * Hlídá, jestli se data nepřekrývají.
	 * Hlídá, jestli splňuje podmínky pro minulá a budoucí data (allowPast, allowFuture)
	 * @param {Event} e Event vyvolaný při změně inputu.
	 */
	changeDateInputTo(e) {
		this.dateTo = Dates.stringToTimestamp(e.target.value);
		this.select.setSelectOption('other');

		let isValid = true;

		if (!this.isStringFromInputValid(e.target.value)) {
			isValid = false;
		} else if (this.allowInterval && this.dateFrom > this.dateTo) {
			isValid = false;
		} else if (!this.allowPast && this.dateTo < this.today) {
			isValid = false;
		} else if (!this.allowFuture && this.dateTo > this.today) {
			isValid = false;
		}

		if (isValid) {
			this.inputTo.classList.remove('is-invalid');
			this.inputTo.valid = true;
			this.validateSecondInput(this.inputFrom, this.dateFrom);
		} else {
			this.inputTo.classList.add('is-invalid');
			this.inputTo.valid = false;
			this.cancelActive();
		}
	}

	/**
	 * Uživatel vybral konkrétní měsíc, takže bude označen pouze ten.
	 * @param {Event} e Event vyvolaný při výběru měsíce.
	 */
	monthSelect(e) {
		// Kontrola zda měsíc není mimo allowPast nebo allowFuture.
		if (!this.allowInterval) {
			return;
		} else if (!this.allowPast && e.target.date.to < this.today) {
			return;
		} else if (!this.allowFuture && e.target.date.from > this.today) {
			return;
		}

		// Nastaví data
		this.dateFrom = e.target.date.from;
		this.dateTo = e.target.date.to;

		if (!this.allowPast && e.target.date.from < this.today) {
			this.dateFrom = this.today;
		}

		if (!this.allowFuture && e.target.date.to > this.today) {
			this.dateTo = this.today;
		}

		this.setInputs();
		this.redrawDays();
		this.select.setSelectOption('other');
	}

	/**
	 * Uživatel vybral konkrétní den.
	 * Je potřeba počítat s tím, že jednou je vybíráno
	 * datum OD a poté DO. Výběr dat se také může 'nelogicky' překrývat.
	 * @param {Event} e Event vyvolaný při výběru dne.
	 */
	daySelect(e) {
		const elem = e.target.parentNode;

		if (elem && elem.classList.contains('Calendar-day') && elem.classList.contains('is-enabled')) {
			elem.classList.toggle('is-active');

			if (this.allowInterval) {
				if (this.selectFrom && elem.timeStamp < this.dateTo) {
					this.dateFrom = elem.timeStamp;
				} else if (this.selectFrom) {
					this.dateTo = elem.timeStamp;
					this.dateFrom = elem.timeStamp;
				} else if (elem.timeStamp > this.dateFrom) {
					this.dateTo = elem.timeStamp;
				} else {
					this.dateTo = this.dateFrom;
					this.dateFrom = elem.timeStamp;
				}

				this.selectFrom = !this.selectFrom;

				this.select.setSelectOption('other');
			} else {
				this.dateFrom = elem.timeStamp;
			}

			this.redrawDays();
			this.highlightInput();
			this.setInputs();
		}
	}

	/**
	 * Odstraní tabulky s datem, protože budou vytvořeny nové.
	 */
	clearTables() {
		if (this.table1) {
			Tools.removeElement(this.table1);
		}

		if (this.table2) {
			Tools.removeElement(this.table2);
		}

		if (this.table3) {
			Tools.removeElement(this.table3);
		}
	}

	/**
	 * Reaguje, pokud uživatel klikne na input.
	 * V takovém případě chceme změnit focus inputu, ale hlavně změnit výběr dat OD/DO.
	 * @param {Event} e Event vyvolaný při výběru inputu.
	 */
	selectInput(e) {
		if (e.target.classList.contains('Calendar-input-dateFrom')) {
			this.selectFrom = true;
			this.inputFrom.classList.add('is-active');

			if (this.allowInterval) {
				this.inputTo.classList.remove('is-active');
			}
		} else {
			this.selectFrom = false;
			this.inputFrom.classList.remove('is-active');
			this.inputTo.classList.add('is-active');
		}
	}

	/**
	 * Vygeneruje input pro zadání data OD.
	 */
	generateInputFrom() {
		this.inputFrom = Tools.createElement('input', {
			class: 'Calendar-input-dateFrom is-active',
			type: 'text',
		});

		this.inputFrom.valid = true;

		this.inputFrom.addEventListener('input', this.changeDateInputFrom.bind(this));

		this.inputFrom.addEventListener('click', this.selectInput.bind(this));
	}

	/**
	 * Vygeneruje input pro zadání data DO.
	 */
	generateInputTo() {
		this.inputTo = Tools.createElement('input', {
			class: 'Calendar-input-dateTo',
			type: 'text',
		});

		this.inputTo.valid = true;
		this.inputTo.addEventListener('input', this.changeDateInputTo.bind(this));
		this.inputTo.addEventListener('click', this.selectInput.bind(this));
	}

	/**
	 * Vygeneruje inputy pro zadávání data od/do a obalí je DIVem.
	 * @returns {Element} Vrací inputsWrapper.
	 */
	generateInputs() {
		const inputsWrapper = Tools.createElement('div', {
			class: 'Calendar-inputs-wrapper',
		});

		const dash = Tools.createElement('div', {
			class: 'Calendar-inputs-dash',
			innerText: '–',
		});

		this.generateInputFrom();
		Tools.appendChilds(inputsWrapper, this.inputFrom);

		if (this.allowInterval) {
			this.generateInputTo();
			Tools.appendChilds(inputsWrapper, dash, this.inputTo);
		}

		return inputsWrapper;
	}

	/**
	 * Vygeneruje do DOMu inputy s časovými údaji.
	 */
	generateFormInputs() {
		if (this.allowInterval) {
			this.dateElement.querySelector('.Calendar-input-period').value = this.select.getSelectedOption();

			this.dateElement.querySelector('.Calendar-input-periodTo').value = Dates.timestampToString(this.dateTo);
		}

		this.dateElement.querySelector('.Calendar-input-periodFrom').value = Dates.timestampToString(this.dateFrom);
	}

	/**
	 * Naplní tlačítko kalendáře novým datem a zavře kalendář.
	 */
	confirmDate() {
		if (!this.inputFrom.valid || (this.allowInterval && !this.inputTo.valid)) {
			return;
		}

		if (this.separated) {
			this.dateElement.querySelector('.js-Calendar-start').innerText = Dates.timestampToString(this.dateFrom);

			this.dateElement.querySelector('.js-Calendar-end').innerText = Dates.timestampToString(this.dateTo);
		} else {
			const wholeDateString = this.makeCalendarButtonString(
				Dates.timestampToString(this.dateFrom),
				Dates.timestampToString(this.dateTo),
			);

			this.setTextOrValue(this.dateElement, wholeDateString);
		}

		if (this.allowInterval) {
			this.dateElement.setAttribute('data-calendar-period-option', this.select.getSelectedOption());
		}

		// Pokud existuje sekce s inputy, taky do ní hodnoty budou vygenerovány.
		const inputs = this.dateElement.querySelector('.Calendar-hidden-inputs');

		if (inputs) {
			this.generateFormInputs();
		}

		this.toggleCalendar();
		this.notifyChange();
	}

	/**
	 * Vytvoří string s intervalem dat nebo jedním datem
	 * @param {String} start Datum od ve stringu
	 * @param {String} end Datum do ve stringu
	 * @returns {String} String jednoho data nebo intervalu s pomlčkou
	 */
	makeCalendarButtonString(start, end) {
		let calendarButtonString = start;

		if (this.allowInterval && end) {
			calendarButtonString += ' – ' + end;
		}

		return calendarButtonString;
	}

	/**
	 * Zavře kalendář beze změn.
	 */
	cancelDate() {
		this.dateFrom = this.originalDateFrom;

		if (this.allowInterval) {
			this.dateTo = this.originalDateTo;
		}

		this.toggleCalendar();
	}

	/**
	 * Vygeneruje tlačítka pro zrušení & potvrzení data.
	 * @returns {Element} Vrací buttonsWrapper.
	 */
	generateConfirmCancel() {
		const buttonsWrapper = Tools.createElement('div', {
			class: 'Calendar-buttons',
		});
		this.changeButton = Tools.createElement('button', {
			class: 'Calendar-changeButton',
			innerText: window.SSP.translations.calendar.additional.buttonChange,
			type: 'button',
		});
		this.cancelButton = Tools.createElement('button', {
			class: 'Calendar-cancelButton',
			innerText: window.SSP.translations.calendar.additional.buttonCancel,
			type: 'button',
		});

		this.changeButton.addEventListener('click', this.confirmDate.bind(this));
		this.cancelButton.addEventListener('click', this.cancelDate.bind(this));

		Tools.appendChilds(buttonsWrapper, this.changeButton, this.cancelButton);

		return buttonsWrapper;
	}

	/**
	 * Vygeneruje formulář, který obsahuje:
	 * - dva inputy pro zadání data (bez allowInterval jen jeden)
	 * - select pro výběr období, pokud allowInterval
	 * - tlačítka pro potvrzení/zrušení
	 * @param {Element} calendarFormWrap
	 */
	generateForm(calendarFormWrap) {
		this.form = Tools.createElement('form');

		const title = Tools.createElement('h6', {
			class: 'Calendar-title',
			innerText: this.allowInterval
				? window.SSP.translations.calendar.additional.labelTitle
				: window.SSP.translations.calendar.additional.labelTitleSingleDate,
		});

		if (this.allowInterval) {
			this.select = new Select();

			this.select.initSelectWithParams(
				window.SSP.translations.calendar.intervals,
				this.changeSelectInterval.bind(this),
				this.dateElement.getAttribute('data-calendar-period-option'),
				'Calendar-select',
				'Calendar-select-button',
				'Calendar-options',
				'Calendar-option',
			);
		}

		const inputsWrapper = this.generateInputs();
		const buttonsWrapper = this.generateConfirmCancel();

		Tools.appendChilds(this.form, title);

		if (this.allowInterval) {
			Tools.appendChilds(this.form, this.select.getElement());
		}

		Tools.appendChilds(this.form, inputsWrapper, buttonsWrapper);
		calendarFormWrap.appendChild(this.form);
	}

	/**
	 * Vygeneruje tlačítka pro práci s kalendářem.
	 */
	generatePrevNextButtons() {
		const prevMonthButton = Tools.createElement('span', {
			class: 'Calendar-prevMonthButton',
		});

		const nextMonthButton = Tools.createElement('span', {
			class: 'Calendar-nextMonthButton',
		});

		prevMonthButton.addEventListener('click', this.prevMonth.bind(this));
		nextMonthButton.addEventListener('click', this.nextMonth.bind(this));

		Tools.appendChilds(this.calendarMonthsWrap, prevMonthButton, nextMonthButton);
	}

	/**
	 * Překreslí dny. Vybrané dny zvýrazní.
	 */
	redrawDays() {
		const days = Array.from(this.calendarMonthsWrap.querySelectorAll('.Calendar .Calendar-day.is-enabled'));

		let firstDay = null;
		let lastDay = null;

		days.forEach((day) => {
			if (
				(this.allowInterval && day.timeStamp >= this.dateFrom && day.timeStamp <= this.dateTo) ||
				day.timeStamp === this.dateFrom
			) {
				if (!firstDay) {
					firstDay = day;
				}

				lastDay = day;
				day.classList.add('is-active');
			} else {
				day.classList.remove('is-active');
			}

			day.classList.remove('is-first');
			day.classList.remove('is-last');
		});

		if (firstDay && lastDay) {
			firstDay.classList.add('is-first');
			lastDay.classList.add('is-last');
		}
	}

	/**
	 * Při vybírání konkrétních dnů v kalendáři je potřeba
	 * změnit zvýraznění inputů, aby uživatel vědět jestli
	 * následujím klikem vybírá datum OD nebo DO.
	 */
	highlightInput() {
		if (this.allowInterval) {
			this.inputFrom.classList.toggle('is-active');
			this.inputTo.classList.toggle('is-active');
		} else {
			this.inputFrom.classList.add('is-active');
		}
	}

	/**
	 * Pokud datum splňuje allowPast a allowFuture, tak reaguje na kliknutí.
	 * V opačném případě je označen jako disabled a má šedou barvu.
	 * @param {Element} elem Buňka s datem.
	 * @param {number} day Číslo dne v měsíci.
	 * @param {number} month Číslo měsíce.
	 * @param {number} year Rok.
	 */
	setDayStatus(elem, day, month, year) {
		let newDate = new Date();

		newDate.setFullYear(year, month, day);
		newDate.setHours(0, 0, 0, 0);
		newDate = newDate.getTime();
		elem.timeStamp = newDate;

		if (this.today.getTime() === newDate) {
			elem.classList.add('is-enabled');
		} else if (this.allowPast && newDate < this.today) {
			elem.classList.add('is-enabled');
		} else if (this.allowFuture && newDate > this.today) {
			elem.classList.add('is-enabled');
		} else {
			elem.classList.add('is-disabled');
		}
	}

	/**
	 * Vytvoří hlavičku měsíce
	 * Listopad 2017
	 * Po Út St ..
	 * @param {number} month Měsíc.
	 * @param {number} year Rok.
	 * @returns {Element} Vrací thead tabulky
	 */
	createHeader(month, year) {
		const thead = Tools.createElement('thead');

		const monthRow = Tools.createElement('tr', {
			class: 'Calendar-months-title',
		});

		// Listopad 2017, navíc buňka obsahuje parametr date,
		// v kterém jsou timestampy, které se využívají při vybrání měsíce
		const monthCell = Tools.createElement('th', {
			colspan: 7,
			innerText: window.SSP.translations.calendar.months[month] + ' ' + year,
		});

		monthCell.date = {
			from: new Date(year, month, 1).getTime(),
			to: new Date(year, month + 1, 0).getTime(),
		};

		monthRow.appendChild(monthCell);

		if (this.allowInterval) {
			monthRow.addEventListener('click', this.monthSelect.bind(this));
		}

		// Po Út St ..
		const daysRow = Tools.createElement('tr', { class: 'Calendar-days-title' });

		window.SSP.translations.calendar.weekdays.forEach((day) => {
			const td = Tools.createElement('td', { innerText: day });

			daysRow.appendChild(td);
		});

		Tools.appendChilds(thead, monthRow, daysRow);

		return thead;
	}

	/**
	 * Vyrenderuje všechny dny v daném měsíci.
	 * @param {number} month 0 = leden
	 * @param {number} year Rok.
	 * @returns {Element} Vrací tabulku s měsícem
	 */
	renderMonth(month, year) {
		const table = Tools.createElement('table', { class: 'Calendar-table' });
		const tbody = Tools.createElement('tbody');
		let tr = Tools.createElement('tr');

		const thead = this.createHeader(month, year);
		const startingDay = Dates.monthStartsAt(year, month);

		for (let i = 0; i < 42; i++) {
			// 42 kouzelný počet dní
			// Buňka s jedním dnem.
			const td = Tools.createElement('td', { class: 'Calendar-day' });

			// Pokud je index (i) uvnitř rozsahu dní v měsíci, tak je buňka naplněna datem.
			if (i >= startingDay && i < Dates.daysInMonth(month, year) + startingDay) {
				const dayNumber = i - (startingDay - 1);
				const span = Tools.createElement('span', { innerText: dayNumber });

				td.appendChild(span);
				this.setDayStatus(td, dayNumber, month, year);
			} else {
				td.innerText = '';
			}

			tr.appendChild(td);

			// Pokud jsem na konci týdne, tak se přesunu na další řádek.
			if (tr.childNodes.length === 7) {
				tbody.appendChild(tr);
				tr = Tools.createElement('tr');
			}
		}

		Tools.appendChilds(table, thead, tbody);

		return table;
	}

	/**
	 * Vyrenderuje 3 měsíce.
	 */
	renderMonths() {
		this.clearTables();

		this.table1 = this.renderMonth(this.visibleRange.first.month, this.visibleRange.first.year);

		this.table2 = this.renderMonth(this.visibleRange.second.month, this.visibleRange.second.year);

		this.table3 = this.renderMonth(this.visibleRange.third.month, this.visibleRange.third.year);

		Tools.appendChilds(this.calendarMonthsWrap, this.table1, this.table2, this.table3);

		this.redrawDays();
	}

	// ----------------------- Výběr konkrétních období --------------------------------

	/**
	 * Nastaví kalendář na dnešní den.
	 */
	selectToday() {
		this.dateTo = this.today.getTime();
		this.dateFrom = this.today.getTime();
	}

	/**
	 * Nastaví kalendář na včerejší den.
	 */
	selectYesterday() {
		const yesterday = this.today - 1000 * 60 * 60 * 24;

		this.dateFrom = yesterday;
		this.dateTo = yesterday;
	}

	/**
	 * Nastaví kalendář na po - dnes.
	 */
	selectWeek() {
		let dayIndex = this.today.getDay();

		// getDay vrací pro neděli = 0, což je nevhodné
		if (dayIndex === 0) {
			dayIndex = 6;
		} else {
			dayIndex = dayIndex - 1;
		}

		// dnešek do současnosti
		this.dateTo = this.today.getTime();

		// začátek týdne
		const startOfWeek = new Date(this.today.getTime());

		startOfWeek.setDate(startOfWeek.getDate() - dayIndex);
		startOfWeek.setHours(0, 0, 0, 0);
		this.dateFrom = startOfWeek.getTime();
	}

	/**
	 * Nastaví kalendář na minulý týden
	 * https://stackoverflow.com/questions/13681702/javascript-get-monday-and-sunday-of-the-previous-week
	 */
	selectLastweek() {
		const beforeOneWeek = new Date(this.today - 60 * 60 * 24 * 7 * 1000);
		const day = beforeOneWeek.getDay();
		const diffToMonday = beforeOneWeek.getDate() - day + (day === 0 ? -6 : 1);
		const lastMonday = new Date(beforeOneWeek.setDate(diffToMonday));

		const lastSunday = new Date(beforeOneWeek.setDate(beforeOneWeek.getDate() + 6));

		lastMonday.setHours(0, 0, 0, 0);
		lastSunday.setHours(23, 59, 59, 999);
		this.dateFrom = lastMonday.getTime();
		this.dateTo = lastSunday.getTime();
	}

	/**
	 * Vybere posledních 7 dní.
	 */
	selectLast7() {
		this.dateFrom = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() - 6).getTime();

		this.dateTo = this.today.getTime();
	}

	/**
	 * Vybere poslední pracovní týden PO-PA.
	 */
	selectLastWorkWeek() {
		const beforeOneWeek = new Date(this.today - 60 * 60 * 24 * 7 * 1000);
		const day = beforeOneWeek.getDay();
		const diffToMonday = beforeOneWeek.getDate() - day + (day === 0 ? -6 : 1);
		const lastMonday = new Date(beforeOneWeek.setDate(diffToMonday));

		const lastFriday = new Date(beforeOneWeek.setDate(beforeOneWeek.getDate() + 4));

		lastMonday.setHours(0, 0, 0, 0);
		lastFriday.setHours(23, 59, 59, 999);
		this.dateFrom = lastMonday.getTime();
		this.dateTo = lastFriday.getTime();
	}

	/**
	 * Vybere celý aktuální měsíc.
	 */
	selectMonth() {
		this.dateFrom = new Date(this.today.getFullYear(), this.today.getMonth(), 1).getTime();

		this.dateTo = this.today.getTime();
	}

	/**
	 * Vybere celý předchozí měsíc.
	 */
	selectLastMonth() {
		this.dateFrom = new Date(this.today.getFullYear(), this.today.getMonth() - 1, 1).getTime();

		const endOfMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 0);

		endOfMonth.setHours(23, 59, 59, 999);
		this.dateTo = endOfMonth.getTime();
	}

	/**
	 * Vybere posledních 6 měsíců.
	 */
	selectLast6Month() {
		this.dateFrom = new Date(this.today.getFullYear(), this.today.getMonth() - 6, 1).getTime();

		const endOfMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 0);

		endOfMonth.setHours(23, 59, 59, 999);
		this.dateTo = endOfMonth.getTime();
	}

	/**
	 * Vrátí měsíc, kterým začíná kvartál.
	 * @returns {number}
	 */
	getQuarterStartingMonth() {
		const actualMonth = this.today.getMonth() + 1; // 1 = leden
		let startingMonth = null;

		switch (actualMonth % 3) {
			case 0:
				startingMonth = actualMonth - 2;

				break;
			case 1:
				startingMonth = actualMonth;

				break;
			case 2:
				startingMonth = actualMonth - 1;

				break;
		}

		return startingMonth - 1; // -1 => posuneme zpět 0 = leden
	}

	/**
	 * Vybere aktuální čtvrtletí.
	 */
	selectQuarter() {
		const startingMonth = this.getQuarterStartingMonth();

		this.dateFrom = new Date(this.today.getFullYear(), startingMonth, 1);
		this.dateTo = this.today;
	}

	/**
	 * Vybere poslední 4 kvartály.
	 */
	selectLast4Quarters() {
		const startingMonth = this.getQuarterStartingMonth();

		this.dateFrom = new Date(this.today.getFullYear(), startingMonth - 9, 1);
		this.dateTo = this.today;
	}

	/**
	 * Vybere posledních 30 dní.
	 */
	selectLast30() {
		this.dateTo = this.today.getTime();

		const dateFrom = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() - 29);

		this.dateFrom = dateFrom.getTime();
	}

	/**
	 * Vybere datum od začátku roku.
	 */
	selectYear() {
		this.dateFrom = new Date(this.today.getFullYear(), 0).getTime();
		this.dateTo = this.today.getTime();
	}

	/**
	 * Vybere celý minulý rok.
	 */
	selectLastYear() {
		this.dateFrom = new Date(this.today.getFullYear() - 1, 0).getTime();

		const endOfYear = new Date(this.today.getFullYear() - 1, 12, 0);

		endOfYear.setHours(23, 59, 59, 999);
		this.dateTo = endOfYear.getTime();
	}

	/**
	 * Období od 1.1.2006 do dneška.
	 */
	selectWhole() {
		this.dateTo = this.today.getTime();
		this.dateFrom = new Date(2006, 0, 1).getTime();
	}

	/**
	 * Vybere konkrétní časové období.
	 * @param {string} selected Vybraná položka v selectu předdefinovaných období
	 * vyvolaný při změně intervalu v select boxu.
	 */
	changeSelectInterval(selected) {
		switch (selected) {
			case 'today':
				this.selectToday();

				break;
			case 'yesterday':
				this.selectYesterday();

				break;
			case 'week':
				this.selectWeek();

				break;
			case 'lastweek':
				this.selectLastweek();

				break;
			case 'last7':
				this.selectLast7();

				break;
			case 'lastworkweek':
				this.selectLastWorkWeek();

				break;
			case 'month':
				this.selectMonth();

				break;
			case 'lastmonth':
				this.selectLastMonth();

				break;
			case 'last6months':
				this.selectLast6Month();

				break;
			case 'quarter':
				this.selectQuarter();

				break;
			case 'last4quarters':
				this.selectLast4Quarters();

				break;
			case 'last30':
				this.selectLast30();

				break;
			case 'year':
				this.selectYear();

				break;
			case 'lastyear':
				this.selectLastYear();

				break;
			case 'whole':
				this.selectWhole();

				break;
		}

		if (selected !== 'other') {
			this.makeInputsValid();
			this.setVisibleRange();
			this.renderMonths();
			this.setInputs();
		}

		this.select.setSelectOption(selected);
		this.select.toggleSelect();
	}

	/**
	 * Pokud uživatel vybral časový interval,
	 * tak je potřeba inputy nastavit, jako validní pro případ,
	 * že byly nevalidní.
	 */
	makeInputsValid() {
		this.inputFrom.valid = true;

		if (this.allowInterval) {
			this.inputTo.valid = true;
		}

		this.inputFrom.classList.remove('is-invalid');
		this.inputTo.classList.remove('is-invalid');
	}

	/**
	 * Kontroluje, zda je rok v povolených mezích.
	 * @param {number} year
	 * @returns {boolean}
	 */
	_isYearValid(year) {
		const actualYear = this.today.getFullYear();

		if (!this.allowPast && year < actualYear) {
			return false;
		} else if (!this.allowFuture && year > actualYear) {
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Validuje datum zadané uživatelem.
	 * Akceptuje formáty:
	 * 1.2.2000, 01.02.2000
	 * @param {string} str Input s datem.
	 * @returns {boolean} Vrací true pokud je daný string validní.
	 */
	isStringFromInputValid(str) {
		const dateReg = /^\d{1,2}[.]\d{1,2}[.]\d{4}$/;

		if (str.match(dateReg)) {
			const dateParts = str.split('.');
			const year = Number(dateParts[2]);
			const month = Number(dateParts[1]);
			const day = Number(dateParts[0]);

			if (!this._isYearValid(year)) {
				return false;
			} else if (month > 12 || month < 1) {
				return false;
			} else if (day < 0 || day > Dates.daysInMonth(month - 1, year)) {
				return false;
			} else {
				return true;
			}
		} else {
			return false;
		}
	}

	/**
	 * Pokud je kalendář otevřený a uživatel klikne mimo, tak se uzavře.
	 */
	listenOnOutsideClick() {
		document.addEventListener('click', (e) => {
			if (this.dateElement.classList.contains('is-open')) {
				if (!e.target.closest('.Calendar') && !e.target.closest('.js-Calendar')) {
					this.cancelDate();
				}
			}
		});
	}
}

export default Calendar;
